import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { OrderSummary } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { OrderLabelChips } from '@/features/production';

import {
  AuctionOrderBadge,
  FinancialStatusBadge,
  FulfillmentStatusBadge,
  OrderStatusBadge,
} from './order-status-badges';

/** Column model of the orders table; `meta` ties columns to the list's sorts and filters. */
export function useOrderColumns(storeId: string) {
  const { t } = useTranslation(['orders', 'production']);
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<OrderSummary>>>(
    () => [
      {
        id: 'name',
        header: t('columns.order'),
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Link
              to="/s/$storeId/orders/$orderId"
              params={{ storeId, orderId: row.original.id }}
              className="font-mono text-[13px] font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.name}
            </Link>
            {row.original.paymentMethod === 'auction' ? <AuctionOrderBadge /> : null}
          </span>
        ),
        meta: { label: t('columns.order'), sortField: 'name', lockVisibility: true },
        size: 170,
      },
      {
        id: 'placedAt',
        accessorKey: 'placedAt',
        header: t('columns.placed'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.placedAt)}>
            {format.relative(row.original.placedAt)}
          </span>
        ),
        meta: { sortField: 'placedAt', filterKey: 'placedAt' },
        size: 130,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: t('columns.customer'),
        cell: ({ row }) =>
          row.original.email ? (
            <span className="block truncate">{row.original.email}</span>
          ) : (
            <span className="text-muted-foreground">{t('walkIn')}</span>
          ),
        meta: { filterKey: 'customerId' },
      },
      {
        id: 'total',
        header: t('columns.total'),
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{format.money(row.original.total)}</span>
        ),
        meta: { sortField: 'total', filterKey: 'total', align: 'end' },
        size: 120,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status' },
        size: 120,
      },
      {
        id: 'financialStatus',
        accessorKey: 'financialStatus',
        header: t('columns.payment'),
        cell: ({ row }) => <FinancialStatusBadge status={row.original.financialStatus} />,
        meta: { filterKey: 'financialStatus', tour: 'orders.list.status-badges' },
        size: 130,
      },
      {
        id: 'fulfillmentStatus',
        accessorKey: 'fulfillmentStatus',
        header: t('columns.fulfillment'),
        cell: ({ row }) => <FulfillmentStatusBadge status={row.original.fulfillmentStatus} />,
        meta: { filterKey: 'fulfillmentStatus' },
        size: 140,
      },
      {
        id: 'production',
        header: t('production:list.column'),
        cell: ({ row }) => {
          const workflow = row.original.workflow;
          if (workflow?.status !== 'active') return null;
          return (
            <span className="flex flex-col gap-1">
              <span className="truncate text-sm">{workflow.stageName}</span>
              {workflow.dueAt ? (
                <span className="text-muted-foreground text-xs" title={format.utc(workflow.dueAt)}>
                  {t('production:list.due', { when: format.dateTime(workflow.dueAt) })}
                </span>
              ) : null}
              <OrderLabelChips labels={workflow.labels} />
            </span>
          );
        },
        size: 190,
      },
      {
        id: 'tags',
        header: t('columns.tags'),
        cell: ({ row }) =>
          row.original.tags.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {row.original.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="font-normal">
                  {tag}
                </Badge>
              ))}
            </span>
          ) : null,
        meta: { filterKey: 'tags' },
        size: 160,
      },
      {
        id: 'lineCount',
        accessorKey: 'lineCount',
        header: t('columns.lines'),
        cell: ({ row }) => (
          <span className="text-muted-foreground tabular-nums">
            {t('columns.lineCount', { count: row.original.lineCount })}
          </span>
        ),
        size: 90,
      },
    ],
    [format, storeId, t],
  );
}
